import { PendingMappingChange } from "../../redux/slice/FeatureSlice";
import { UpdateFeatureClientMappingPayload } from "../../service/Api/FeatureApi";

export type SettingItem = {
  id: string;
  label: string;
  icon: string;
  enabled: boolean;
  hasSubsections?: boolean;
};

export type SubsectionState = Record<string, boolean>;

export type ApiFeature = {
  id: string;
  label: string;
};

export type ApiMenu = {
  id: string;
  label: string;
  features: ApiFeature[];
  sequence?: number;
};

export type MemberTypeFilter = {
  id: string;
  label: string;
};

export const MEMBER_TYPE_FILTERS: MemberTypeFilter[] = [
  { id: "all", label: "All" },
  { id: "medicare", label: "Medicare" },
  { id: "commercial", label: "Commercial" },
  { id: "aso", label: "ASO" },
];

export const MEMBER_TYPE_API_VALUES: Record<string, string> = {
  medicare: "Medicare",
  commercial: "Commercial",
  aso: "ASO",
};

export const isProminenceClientName = (clientName?: string | null) =>
  (clientName || "").trim().toLowerCase() === "prominence";

export const getVisibleMemberTypeFilters = (
  isProminence: boolean,
): MemberTypeFilter[] =>
  isProminence
    ? MEMBER_TYPE_FILTERS.filter((filter) => filter.id !== "all")
    : MEMBER_TYPE_FILTERS.filter((filter) => filter.id === "all");

/**
 * Member-type resolution for GET/POST.
 * All tab omits member type (empty array) so APIs are called without it.
 * Specific tabs (Medicare / ASO / Commercial) pass that type only.
 */
export const resolveMemberTypesForTab = (
  tabId: string,
  isProminence: boolean,
): string[] => {
  if (tabId === "all") {
    return [];
  }

  if (!isProminence) {
    return [MEMBER_TYPE_API_VALUES.medicare];
  }

  return [MEMBER_TYPE_API_VALUES[tabId] ?? MEMBER_TYPE_API_VALUES.medicare];
};

/** GET uses the same member-type resolution as POST */
export const resolveMemberTypesForGet = (
  tabId: string,
  isProminence: boolean,
): string[] => resolveMemberTypesForTab(tabId, isProminence);

/**
 * UpdateMenuSequence validates MemberType as required with a minimum length of
 * 1, so the All tab defaults to Medicare.
 *
 * Use this same value for the post-sequence mapping restore (isDeleted) that
 * turns menus/features back off. Sequence upsert re-enables listed menus under
 * this member type; restoring without it leaves them enabled (ANC-5050).
 */
export const resolveMemberTypesForSequenceSave = (
  tabId: string,
  isProminence: boolean,
): string[] => {
  const memberTypes = resolveMemberTypesForTab(tabId, isProminence);
  return memberTypes.length
    ? memberTypes
    : [MEMBER_TYPE_API_VALUES.medicare];
};

export const toNumericId = (value: string) => {
  if (!value?.trim()) return null;
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
};

export const applyPendingOverlayForTab = (
  tabId: string,
  baseSettings: SettingItem[],
  baseSubsections: SubsectionState,
  pendingChanges: Record<string, PendingMappingChange>,
): { settings: SettingItem[]; subsections: SubsectionState } => {
  const tabChanges = Object.values(pendingChanges).filter(
    (change) => change.sourceTabId === tabId,
  );

  if (!tabChanges.length) {
    return { settings: baseSettings, subsections: baseSubsections };
  }

  const settings = baseSettings.map((item) => {
    const menuChange = tabChanges.find(
      (change) =>
        change.entityType === "menu" && String(change.entityId) === item.id,
    );
    if (!menuChange) return item;
    return { ...item, enabled: !menuChange.isDeleted };
  });

  const subsections = { ...baseSubsections };
  tabChanges.forEach((change) => {
    if (change.entityType === "feature") {
      subsections[String(change.entityId)] = !change.isDeleted;
    }
  });

  return { settings, subsections };
};

export const groupPendingChangesForSave = (
  changes: PendingMappingChange[],
  clientId: number,
): UpdateFeatureClientMappingPayload[] => {
  const groups = new Map<string, PendingMappingChange[]>();

  changes.forEach((change) => {
    const key = `${change.isDeleted}::${change.memberType.join("|")}`;
    const existing = groups.get(key);
    if (existing) {
      existing.push(change);
    } else {
      groups.set(key, [change]);
    }
  });

  return Array.from(groups.values()).map((group) => {
    const memberType = group[0].memberType;

    return {
      clientId,
      menuId: group
        .filter((item) => item.entityType === "menu")
        .map((item) => item.entityId),
      featureIds: group
        .filter((item) => item.entityType === "feature")
        .map((item) => item.entityId),
      isDeleted: group[0].isDeleted,
      ...(memberType.length ? { memberType } : {}),
    };
  });
};

/** Union menus/features across payloads — presence means enabled; order ignored */
export const mergeApplicationFeaturePayloads = (
  payloads: unknown[],
  normalizeMenusAndFeatures: (payload: unknown) => ApiMenu[],
): ApiMenu[] => {
  const menusById = new Map<string, ApiMenu>();

  payloads.forEach((payload) => {
    normalizeMenusAndFeatures(payload).forEach((menu) => {
      const existing = menusById.get(menu.id);
      if (!existing) {
        menusById.set(menu.id, {
          id: menu.id,
          label: menu.label,
          features: [...menu.features],
        });
        return;
      }

      const featureIds = new Set(existing.features.map((feature) => feature.id));
      menu.features.forEach((feature) => {
        if (!featureIds.has(feature.id)) {
          existing.features.push(feature);
          featureIds.add(feature.id);
        }
      });
    });
  });

  // Order is intentionally not taken from ApplicationFeaturesAndMenus
  return Array.from(menusById.values());
};

/** Order settings by catalog list order from GetAllMenusAndFeatures */
export const sortSettingsByApiMenuOrder = (
  settings: SettingItem[],
  orderedMenus: ApiMenu[],
): SettingItem[] => {
  const orderIndex = new Map<string, number>();

  // Prefer response array order over Sequence values (those can lag after save)
  orderedMenus.forEach((menu, index) => {
    const rank = index + 1;
    orderIndex.set(menu.id, rank);
    orderIndex.set(menu.label.trim().toLowerCase(), rank);
  });

  return [...settings].sort((a, b) => {
    const aRank =
      orderIndex.get(a.id) ??
      orderIndex.get(a.label.trim().toLowerCase()) ??
      Number.MAX_SAFE_INTEGER;
    const bRank =
      orderIndex.get(b.id) ??
      orderIndex.get(b.label.trim().toLowerCase()) ??
      Number.MAX_SAFE_INTEGER;
    return aRank - bRank;
  });
};
